'use client'

// ---------------------------------------------------------------------------
// Community library picker — each root folder of the published source
// workspace is a pack (a coherent kit: cast + sets + props + look). Users
// pick packs, not single collections, so kits stay whole; packs already in
// this workspace read as owned and can't double-import. Content comes live
// from GET /library/community/snapshot (bundled examples as the dev
// fallback), so what this dialog shows is what the source holds right now.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react'

import { Check } from 'lucide-react'

import {
  buildSampleLibrary,
  normalizeCommunityLibrary,
  type CommunitySeed,
} from '@/components/collections/sampleLibrary'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useLibraryStore } from '@/store/libraryStore'
import { fetchCommunityLibrarySnapshot } from '@/store/libraryTransport'

const rootOf = (folder: string | null) => folder?.split('/')[0] ?? ''

type Pack = {
  root: string
  label: string
  collectionCount: number
  itemCount: number
  heroes: string[]
  owned: boolean
}

export function CommunityLibraryDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const collections = useLibraryStore(s => s.collections)
  const importCommunityLibrary = useLibraryStore(s => s.importCommunityLibrary)
  const [seed, setSeed] = useState<CommunitySeed | null>(null)
  // null = default selection (every pack not yet owned); a Set materializes
  // on first toggle. Derived, so no state-sync effect is needed.
  const [selected, setSelected] = useState<Set<string> | null>(null)
  // An operational fetch failure is NOT "nothing published" — in prod the
  // fallback is empty, so errors get their own state with a retry.
  const [failed, setFailed] = useState(false)
  const loading = open && !seed && !failed

  useEffect(() => {
    // Fetch once per mount — the source is curated, not volatile.
    if (!open || seed || failed) return
    let cancelled = false
    void (async () => {
      const result = await fetchCommunityLibrarySnapshot()
      if (cancelled) return
      if (result.status === 'error') {
        setFailed(true)
        return
      }
      setSeed(
        result.status === 'ok' && result.snapshot.collections.length
          ? normalizeCommunityLibrary(result.snapshot)
          : buildSampleLibrary(),
      )
    })()
    return () => {
      cancelled = true
    }
  }, [open, seed, failed])

  const packs = useMemo<Pack[]>(() => {
    if (!seed) return []
    const live = new Set(collections.filter(c => !c.deletedAt).map(c => c.id))
    const roots = [...new Set(seed.collections.map(c => rootOf(c.folder)))]
    return roots.map(root => {
      const cols = seed.collections.filter(c => rootOf(c.folder) === root)
      const colIds = new Set(cols.map(c => c.id))
      const heroes = cols
        .map(c => {
          const own = seed.assets.filter(a => a.collectionId === c.id)
          const hero = own.find(a => a.id === c.heroAssetId && a.kind === 'image') ?? own.find(a => a.kind === 'image')
          return hero?.src
        })
        .filter((src): src is string => Boolean(src))
        .slice(0, 4)
      return {
        root,
        label: root || 'Unfiled',
        collectionCount: cols.length,
        itemCount: seed.assets.filter(a => colIds.has(a.collectionId)).length,
        heroes,
        owned: cols.every(c => live.has(c.id)),
      }
    })
  }, [seed, collections])

  // Everything not yet owned starts selected — one click still takes it all.
  const effectiveSelected = selected ?? new Set(packs.filter(p => !p.owned).map(p => p.root))

  const close = (o: boolean) => {
    // Drop the materialized selection so the next open re-derives defaults
    // against what the workspace owns by then; a failed fetch retries fresh.
    if (!o) {
      setSelected(null)
      setFailed(false)
    }
    onOpenChange(o)
  }

  const confirm = () => {
    if (seed && effectiveSelected.size) importCommunityLibrary(seed, [...effectiveSelected])
    close(false)
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Community library</DialogTitle>
          <DialogDescription>
            Ready-made worlds — characters, sets, props, and looks that work together. Every copy is yours to edit.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {loading && <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>}
          {failed && (
            <div className="flex flex-col items-center gap-2 py-8">
              <span className="text-sm text-muted-foreground">Couldn&apos;t load the community library.</span>
              <Button size="sm" variant="outline" onClick={() => setFailed(false)}>
                Try again
              </Button>
            </div>
          )}
          {!loading && seed && packs.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">Nothing published yet.</div>
          )}
          {!loading &&
            packs.map(pack => {
              const checked = effectiveSelected.has(pack.root)
              return (
                <button
                  key={pack.root}
                  disabled={pack.owned}
                  onClick={() => {
                    const next = new Set(effectiveSelected)
                    if (checked) next.delete(pack.root)
                    else next.add(pack.root)
                    setSelected(next)
                  }}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border p-2.5 text-start transition-colors',
                    pack.owned
                      ? 'border-border/40 opacity-60'
                      : checked
                        ? 'border-foreground/40 bg-secondary/40'
                        : 'border-border hover:border-foreground/30',
                  )}
                >
                  <div className="flex shrink-0 -space-x-2">
                    {pack.heroes.map((src, i) => (
                      <img
                        key={src}
                        src={src}
                        alt=""
                        loading="lazy"
                        className="size-10 rounded-md border-2 border-background object-cover"
                        style={{ zIndex: pack.heroes.length - i }}
                      />
                    ))}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-foreground">{pack.label}</div>
                    <div className="text-xs tabular-nums text-muted-foreground">
                      {pack.collectionCount} collection{pack.collectionCount === 1 ? '' : 's'} · {pack.itemCount} item
                      {pack.itemCount === 1 ? '' : 's'}
                    </div>
                  </div>
                  {pack.owned ? (
                    <span className="shrink-0 text-xs text-muted-foreground">In your library</span>
                  ) : (
                    <span
                      className={cn(
                        'flex size-4 shrink-0 items-center justify-center rounded border transition-colors',
                        checked ? 'border-foreground bg-foreground text-background' : 'border-muted-foreground/50',
                      )}
                    >
                      {checked && <Check className="size-3" />}
                    </span>
                  )}
                </button>
              )
            })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={!effectiveSelected.size}>
            Add to library
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
